import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ClipboardCheck, Loader2, AlertTriangle, Phone, MapPin, ArrowRight, RotateCcw, ShieldAlert } from 'lucide-react'
import { PortalLayout } from '../../components/portal/PortalLayout'
import { Seo } from '../../components/portal/Seo'
import { Container, Section, SectionHeading, Breadcrumbs, Button, Alert } from '../../components/portal/ui'
import {
  getSymptoms,
  assessSymptoms,
  type Symptom,
  type SymptomAssessment,
  type SymptomRiskLevel,
} from '../../services/symptomService'

// Per-band visual treatment. The band is an advisory cue, NEVER a diagnosis —
// the card copy and permanent disclaimer make that explicit regardless of band.
const BAND_STYLES: Record<SymptomRiskLevel, { ring: string; bar: string; icon: typeof ShieldAlert }> = {
  NONE: { ring: 'ring-emerald-200', bar: 'bg-emerald-500', icon: ClipboardCheck },
  LOW: { ring: 'ring-amber-200', bar: 'bg-amber-500', icon: ClipboardCheck },
  MODERATE: { ring: 'ring-orange-300', bar: 'bg-orange-500', icon: AlertTriangle },
  URGENT: { ring: 'ring-red-300', bar: 'bg-red-500', icon: ShieldAlert },
}

export default function SymptomCheckerPage() {
  const { t, i18n } = useTranslation()
  const [symptoms, setSymptoms] = useState<Symptom[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [loadingCatalog, setLoadingCatalog] = useState(true)
  const [assessing, setAssessing] = useState(false)
  const [result, setResult] = useState<SymptomAssessment | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [catError, setCatError] = useState<string | null>(null)

  // Load the localised symptom catalog once; reload when the UI language changes
  // so labels follow EN/HI/MR.
  useEffect(() => {
    let cancelled = false
    setLoadingCatalog(true); setCatError(null)
    getSymptoms()
      .then((data) => { if (!cancelled) setSymptoms(data) })
      .catch(() => { if (!cancelled) setCatError(t('pages.symptomChecker.catalogError')) })
      .finally(() => { if (!cancelled) setLoadingCatalog(false) })
    return () => { cancelled = true }
  }, [i18n.language, t])

  // If a result is already shown and the language changes, clear it so the user
  // re-checks (the result text is language-specific and would otherwise be stale).
  useEffect(() => { setResult(null) }, [i18n.language])

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    if (result) setResult(null)
  }

  const check = async () => {
    setAssessing(true); setError(null)
    try {
      const data = await assessSymptoms(Array.from(selected))
      setResult(data)
      // Move focus to the result for screen-reader / keyboard users.
      requestAnimationFrame(() => document.getElementById('symptom-result')?.focus())
    } catch (e: any) {
      setError(e?.response?.data?.message || t('pages.symptomChecker.assessError'))
    } finally { setAssessing(false) }
  }

  const reset = () => {
    setSelected(new Set()); setResult(null); setError(null)
    document.getElementById('symptom-check')?.focus()
  }

  const band = result ? BAND_STYLES[result.riskLevel] : null

  return (
    <PortalLayout>
      <Seo
        title={t('pages.symptomChecker.seo.title')}
        description={t('pages.symptomChecker.seo.description')}
        canonicalPath="/symptom-checker"
        breadcrumbs={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.symptomChecker', 'Symptom Checker'), path: '/symptom-checker' }]}
      />

      {/* Hero */}
      <Section className="pb-8">
        <Container>
          <Breadcrumbs items={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.symptomChecker', 'Symptom Checker') }]} />
          <SectionHeading as="h1" eyebrow={t('pages.symptomChecker.hero.eyebrow')} title={t('pages.symptomChecker.hero.title')} description={t('pages.symptomChecker.hero.description')} />
          <div className="mt-4 max-w-2xl">
            <Alert tone="warning" title={t('pages.symptomChecker.notDiagnosis')}>
              {t('pages.symptomChecker.notDiagnosisDesc')}
            </Alert>
          </div>
        </Container>
      </Section>

      {/* Symptom checklist */}
      <Section className="bg-white py-8">
        <Container className="max-w-3xl">
          <h2 id="symptom-check" tabIndex={-1} className="text-xl font-semibold text-slate-800 mb-1">
            {t('pages.symptomChecker.questionTitle')}
          </h2>
          <p className="text-slate-600 mb-6 text-sm">{t('pages.symptomChecker.questionHelp')}</p>

          {catError && <Alert tone="warning" title={catError} className="mb-4" />}

          {loadingCatalog ? (
            <div className="flex items-center gap-2 text-slate-500 py-8" role="status">
              <Loader2 className="w-5 h-5 animate-spin" /> {t('pages.symptomChecker.loading')}
            </div>
          ) : (
            <ul className="space-y-3" aria-describedby="symptom-check">
              {symptoms.map((s) => {
                const checked = selected.has(s.id)
                return (
                  <li key={s.id}>
                    <label
                      className={`flex gap-3 items-start p-4 rounded-xl border cursor-pointer transition min-h-[44px] bg-white hover:bg-slate-50 ${checked ? 'border-[rgb(15_76_129)] bg-[rgb(15_76_129)]/5 ring-1 ring-[rgb(15_76_129)]/30' : 'border-slate-200'}`}
                    >
                      <input
                        type="checkbox"
                        className="mt-1 h-5 w-5 rounded border-slate-300 text-[rgb(15_76_129)] focus:ring-[rgb(15_76_129)] shrink-0"
                        checked={checked}
                        onChange={() => toggle(s.id)}
                        aria-describedby={s.description ? `desc-${s.id}` : undefined}
                      />
                      <span className="flex-1">
                        <span className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-800">{s.label}</span>
                          {s.redFlag && (
                            <span className="inline-flex items-center rounded-full bg-[rgb(254_226_226)] text-[rgb(127_29_29)] px-2.5 py-0.5 text-xs font-semibold">{t('pages.symptomChecker.redFlag')}</span>
                          )}
                        </span>
                        {s.description && (
                          <span id={`desc-${s.id}`} className="block text-sm text-slate-500 mt-0.5">{s.description}</span>
                        )}
                      </span>
                    </label>
                  </li>
                )
              })}
            </ul>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button id="symptom-check-btn" onClick={check} variant="primary" size="lg" disabled={assessing || loadingCatalog} icon={assessing ? <Loader2 className="w-5 h-5 animate-spin" /> : <ClipboardCheck className="w-5 h-5" />} className="min-h-[44px]">
              {assessing ? t('pages.symptomChecker.checking') : t('pages.symptomChecker.checkBtn')}
            </Button>
            {(selected.size > 0 || result) && (
              <Button onClick={reset} variant="ghost" size="md" icon={<RotateCcw className="w-4 h-4" />}>{t('pages.symptomChecker.reset')}</Button>
            )}
            <span className="text-sm text-slate-500" aria-live="polite">
              {t('pages.symptomChecker.selectedCount', { count: selected.size })}
            </span>
          </div>
          {error && <Alert tone="warning" title={error} className="mt-4" />}
        </Container>
      </Section>

      {/* Result */}
      {result && band && (
        <Section className="bg-slate-50 py-8">
          <Container className="max-w-3xl">
            <div
              id="symptom-result"
              tabIndex={-1}
              className={`card ring-2 ${band.ring} bg-white`}
              aria-labelledby="result-heading"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-white ${band.bar}`} aria-hidden="true">
                  <band.icon className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{t('pages.symptomChecker.resultLabel')}</p>
                  <h2 id="result-heading" className="text-lg font-semibold text-slate-800">{result.riskLabel}</h2>
                </div>
              </div>

              <p className="text-slate-700">{result.summary}</p>
              <p className="text-slate-700 mt-3"><span className="font-semibold">{t('pages.symptomChecker.recommendationLabel')} </span>{result.recommendation}</p>

              {result.selectedSymptoms.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">{t('pages.symptomChecker.youSelected')}</p>
                  <div className="flex flex-wrap gap-2">
                    {result.selectedSymptoms.map((label, i) => (
                      <span key={i} className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700">{label}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Action buttons from the backend */}
              <div className="mt-6 flex flex-wrap gap-3">
                {result.actions.map((a, i) =>
                  a.type === 'CALL' ? (
                    <Button key={i} href={a.href || undefined} variant="accent" size="md" icon={<Phone className="w-4 h-4" />} className="min-h-[44px]">{a.label}</Button>
                  ) : (
                    <Button key={i} to={a.path || undefined} variant="primary" size="md" icon={a.path === '/find-tb-centre' ? <MapPin className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />} className="min-h-[44px]">{a.label}</Button>
                  )
                )}
              </div>
            </div>

            {/* Permanent disclaimer — rendered verbatim from the backend */}
            <Alert tone="warning" title={t('pages.symptomChecker.important')} className="mt-4">
              {result.disclaimer}
            </Alert>
          </Container>
        </Section>
      )}
    </PortalLayout>
  )
}